/** @format */

import { forkJoin, Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { selectAttributes, selectSelectedAttributeOptions } from '../+state/attributes.selectors';
import { selectSelectedPersonalizationOptions } from '@pdp/data-access/state';

import { AttributeErrorResponse } from '../interfaces/attributes-utility.interface';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { attributeValidationPerformed } from '../+state/attributes.actions';

@Injectable({
	providedIn: 'root'
})
export class AttributesUtilityService {
	constructor(private store: Store) {}

	/**
	 * @description change a string of words so that the first letter in each word is captialized
	 * @example m  -> M , left hand -> Left Hand
	 */
	capitalizeTheValueToMatchProductDataValue(valueString: string): string {
		const arr = valueString.split(' ');
		for (let i = 0; i < arr.length; i++) {
			arr[i] = arr[i].charAt(0).toUpperCase() + arr[i].slice(1);
		}
		return arr.join(' ');
	}

	/**
	 * @description Retrieves selected attributes and all attribute options from state store.
	 * And checks if any of the attribute options is missing from the selected attributes.
	 * If any are missing it will be added to the error list and dispatches an action to update the store.
	 * @returns true if any errors were added to the list and false otherwise.
	 */
	validateIfAttributeErrorsExist(source: string, isPersonalized = false): boolean {
		let isErrorsOnAttributes = false;
		forkJoin([
			this.store.select(selectSelectedAttributeOptions).pipe(take(1)),
			this.store.select(selectAttributes).pipe(take(1))
		]).subscribe(([selectedAttributes, attributeOptions]) => {
			const errorList = {};
			if (selectedAttributes?.length === attributeOptions?.length) {
				isErrorsOnAttributes = false;
			} else {
				attributeOptions.forEach((attribute) => {
					let isOptionPresent = false;
					selectedAttributes.forEach((selectedAttribute) => {
						if (
							attribute[0].name.toLowerCase() === selectedAttribute.name.toLowerCase()
						) {
							isOptionPresent = true;
						}
					});
					if (!isOptionPresent) {
						errorList[attribute[0].name] = `Please Select ${attribute[0].name}`;
						isErrorsOnAttributes = true;
					}
				});
			}

			if (isPersonalized) {
				this.store
					.select(selectSelectedPersonalizationOptions)
					.pipe(take(1))
					.subscribe((personalizationOptions) => {
						if (!personalizationOptions.valid) {
							errorList['Personalization'] = 'Please enter text';
							isErrorsOnAttributes = true;
						}
					});
			}

			this.store.dispatch(
				attributeValidationPerformed({
					error: isErrorsOnAttributes ? errorList : null,
					source
				})
			);
		});

		return isErrorsOnAttributes;
	}

	/**
	 * @description Retrieves selected attributes and all attribute options from state store.
	 * And checks if any of the attribute options is missing from the selected attributes when trying to favorite a sku.
	 * If any are missing it will be added to the error list and dispatches an action to update the store.
	 * @returns true if any errors were added to the list and false otherwise.
	 */
	validateIfAttributeErrorsExistWhenFavoriting(
		isPersonalized = false
	): Observable<AttributeErrorResponse> {
		let isErrorsOnAttributes = false;
		let savedAttributes = [];
		let personalizedAttributes = null;
		return forkJoin([
			this.store.select(selectSelectedAttributeOptions).pipe(take(1)),
			this.store.select(selectAttributes).pipe(take(1))
		]).pipe(
			map(([selectedAttributes, attributeOptions]) => {
				const errorList = {};
				if (selectedAttributes?.length === attributeOptions?.length) {
					isErrorsOnAttributes = false;
					savedAttributes = selectedAttributes;
				} else if (!selectedAttributes?.length && attributeOptions?.length) {
					isErrorsOnAttributes = false;
					savedAttributes = [];
				} else {
					attributeOptions.forEach((attribute) => {
						let isOptionPresent = false;
						selectedAttributes.forEach((selectedAttribute) => {
							if (
								attribute[0].name.toLowerCase() ===
								selectedAttribute.name.toLowerCase()
							) {
								isOptionPresent = true;
							}
						});
						if (!isOptionPresent) {
							errorList[attribute[0].name] = `Please Select ${attribute[0].name}`;
							isErrorsOnAttributes = true;
						}
					});
				}

				if (isPersonalized) {
					this.store
						.select(selectSelectedPersonalizationOptions)
						.pipe(take(1))
						.subscribe((personalizationOptions) => {
							if (
								!Object.keys(personalizationOptions).length &&
								!selectedAttributes.length
							) {
								// no personalizations set. save at ecode level
								return;
							} else if (!personalizationOptions.valid) {
								errorList['Personalization'] = 'Please enter text';
								isErrorsOnAttributes = true;
							}
							personalizedAttributes = personalizationOptions;
						});
				}

				this.store.dispatch(
					attributeValidationPerformed({
						error: isErrorsOnAttributes ? errorList : null,
						source: 'favorite'
					})
				);

				return {
					isErrorsOnAttributes,
					attributes: savedAttributes,
					personalizedAttributes: personalizedAttributes
				};
			})
		);
	}
}
